import { ArrowDownTrayIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { ActionIcon, Badge, Box, Button, Checkbox, Group, rem, Text } from '@mantine/core';
import { SVGProps, useState } from 'react';
import { useTranslation } from '../../../../hooks/useTranslation';
import { Person } from '../../../../models/person.model';
import { ExportService } from '../../../../services/exportService';
import { GetPersonsPageRequest, PersonService } from '../../../../services/personService';
import { AnonymizationOption, CvExportOptionsModal, ExportFormat, ExportLanguage } from '../../../../components/common/bulk-export';
import { allOnPageSelected, getSelectionTier } from '../../../../utils/selectionManager';

type ExportMode = 'selected' | 'page' | 'all';

const IconDownload = (props: SVGProps<SVGSVGElement>) => <ArrowDownTrayIcon {...props} />;
const IconX = (props: SVGProps<SVGSVGElement>) => <XMarkIcon {...props} />;

interface BulkOperationsManagerProps {
    selectionMode: boolean;
    onEnterSelectionMode: () => void;
    currentPagePersons: Person[];
    selectedIds: Set<number>;
    totalItems: number;
    searchRequest: GetPersonsPageRequest;
    onSelectAllOnPage: () => void;
    onDeselectAllOnPage: () => void;
    onDeselectByIds: (ids: number[]) => void;
    onReselectByIds: (ids: number[]) => void;
    onClearAll: () => void;
    onCancel: () => void;
    hasResults: boolean;
    loading: boolean;
}

export const BulkOperationsManager = ({
    selectionMode,
    onEnterSelectionMode,
    currentPagePersons,
    selectedIds,
    totalItems,
    searchRequest,
    onSelectAllOnPage,
    onDeselectAllOnPage,
    onDeselectByIds,
    onReselectByIds,
    onClearAll,
    onCancel,
    hasResults,
    loading,
}: BulkOperationsManagerProps) => {
    const { t } = useTranslation();
    const exportService = new ExportService();
    const personService = new PersonService();
    const [exportMode, setExportMode] = useState<ExportMode>('selected');
    const [exportModalOpen, setExportModalOpen] = useState(false);

    const selectedCount = selectedIds.size;
    const pagePersonIds = currentPagePersons.map(p => p.id);
    const tier = getSelectionTier(selectedCount);
    const canExportSelected = selectedCount > 0 && tier !== 'hard-block';
    const allPageSelected = allOnPageSelected(selectedIds, pagePersonIds);
    const somePageSelected = !allPageSelected && pagePersonIds.some(id => selectedIds.has(id));

    const openExport = (mode: ExportMode) => {
        setExportMode(mode);
        setExportModalOpen(true);
    };

    // Shared by all 3 modes — mirrors whatever survives the review-and-prune step back onto the
    // page's real, persistent selection (fired incrementally during review, not just once at "Done").
    const syncReviewedIdsToSelection = (ids: number[]) => {
        const idSet = new Set(ids);
        const idsToRemove = Array.from(selectedIds).filter(id => !idSet.has(id));
        const idsToAdd = ids.filter(id => !selectedIds.has(id));

        if (idsToRemove.length > 0) {
            onDeselectByIds(idsToRemove);
        }
        if (idsToAdd.length > 0) {
            onReselectByIds(idsToAdd);
        }
    };

    const exportAllReviewIds = async (): Promise<number[]> => {
        const reviewRequest: GetPersonsPageRequest = {
            ...searchRequest,
            filterModel: {
                ...searchRequest.filterModel,
                pageIndex: 0,
                pageSize: Math.max(1, totalItems),
            },
        };

        try {
            const page = await personService.getPersonsPage(reviewRequest);
            return (page.page || []).map(person => person.id);
        } catch (error) {
            console.error('Failed to prepare review selection:', error);
            alert(t('BULK_ALERT_REVIEW_LOAD_FAILED'));
            return [];
        }
    };

    const exportSelectedCvs = (
        reviewedIds: number[] | null,
        anonymization: AnonymizationOption,
        language: ExportLanguage,
        format: ExportFormat
    ) => exportService.bulkExportByIds(reviewedIds ?? Array.from(selectedIds), anonymization, language, format);

    const exportPageCvs = (
        reviewedIds: number[] | null,
        anonymization: AnonymizationOption,
        language: ExportLanguage,
        format: ExportFormat
    ) => exportService.bulkExportByIds(reviewedIds ?? pagePersonIds, anonymization, language, format);

    const exportAllCvs = (
        reviewedIds: number[] | null,
        anonymization: AnonymizationOption,
        language: ExportLanguage,
        format: ExportFormat
    ) => {
        if (reviewedIds) {
            return exportService.bulkExportByIds(reviewedIds, anonymization, language, format);
        }

        const allRequest: GetPersonsPageRequest = {
            ...searchRequest,
            filterModel: { ...searchRequest.filterModel, pageSize: 1000, pageIndex: 0 },
        };
        return exportService.bulkExport(allRequest, anonymization, language, format);
    };

    if (!hasResults && !loading) {
        return null;
    }

    if (!selectionMode) {
        return (
            <Group justify="flex-end" mb={4}>
                <Button
                    variant="light"
                    color="brand"
                    size="xs"
                    leftSection={<IconDownload style={{ width: rem(16), height: rem(16) }} />}
                    onClick={onEnterSelectionMode}
                    disabled={loading}
                    aria-label={t('BULK_EXPORT_OPEN_OPTIONS')}
                >
                    {t('BULK_EXPORT_LABEL')}
                </Button>
            </Group>
        );
    }

    return (
        <>
            <Box
                mb="xs"
                style={{
                    borderRadius: 8,
                    border: '1px solid var(--mantine-color-orange-5)',
                    background: 'var(--mantine-color-orange-3)',
                    padding: '8px 12px',
                }}
            >
                <Group justify="space-between" align="center" wrap="wrap" gap="xs">
                    {/* Left: page selection + count */}
                    <Group gap="sm" align="center">
                        <Checkbox
                            checked={allPageSelected}
                            indeterminate={somePageSelected}
                            onChange={() => allPageSelected ? onDeselectAllOnPage() : onSelectAllOnPage()}
                            label={<Text size="sm" fw={600} c="dark.8">{t('BULK_SELECT_PAGE')}</Text>}
                            aria-label={allPageSelected ? t('BULK_DESELECT_PAGE_ARIA') : t('BULK_SELECT_PAGE_ARIA')}
                        />
                        {selectedCount > 0 && (
                            <Group gap={4} align="center">
                                <Badge color="brand.6" variant="filled" size="md">
                                    {t('BULK_SELECTED_COUNT', { count: selectedCount.toString() })}
                                </Badge>
                                <ActionIcon
                                    variant="subtle"
                                    color="gray"
                                    size="sm"
                                    onClick={onClearAll}
                                    aria-label={t('BULK_CLEAR_ALL_SELECTIONS')}
                                >
                                    <IconX style={{ width: rem(14), height: rem(14) }} />
                                </ActionIcon>
                            </Group>
                        )}
                    </Group>

                    {/* Right: export actions + cancel */}
                    <Group gap="xs" align="center">
                        <Button
                            size="compact-sm"
                            color="brand.6"
                            variant="filled"
                            leftSection={<IconDownload style={{ width: rem(14), height: rem(14) }} />}
                            onClick={() => openExport('selected')}
                            disabled={!canExportSelected}
                            styles={{
                                root: canExportSelected
                                    ? {
                                        background: 'var(--mantine-color-brand-6)',
                                        color: 'var(--mantine-color-white)',
                                        border: '1px solid var(--mantine-color-brand-7)',
                                    }
                                    : {
                                        opacity: 1,
                                        background: 'rgba(255, 255, 255, 0.72)',
                                        color: 'var(--mantine-color-gray-7)',
                                        border: '1px dashed var(--mantine-color-gray-5)',
                                    },
                                section: canExportSelected
                                    ? {
                                        color: 'var(--mantine-color-white)',
                                    }
                                    : {
                                        color: 'var(--mantine-color-gray-6)',
                                    },
                            }}
                            aria-label={selectedCount > 0
                                ? t('BULK_EXPORT_SELECTED_ARIA', { count: selectedCount.toString() })
                                : t('BULK_SELECT_CVS_TO_EXPORT')}
                        >
                            {t('BULK_EXPORT_SELECTED')}
                        </Button>
                        <Button
                            size="compact-sm"
                            color="brand.7"
                            variant="default"
                            leftSection={<IconDownload style={{ width: rem(14), height: rem(14) }} />}
                            onClick={() => openExport('page')}
                            styles={{
                                root: {
                                    background: 'rgba(255, 255, 255, 0.56)',
                                    color: 'var(--mantine-color-brand-7)',
                                    border: '1px solid var(--mantine-color-orange-5)',
                                },
                                section: {
                                    color: 'var(--mantine-color-brand-7)',
                                },
                            }}
                            aria-label={t('BULK_EXPORT_PAGE_ARIA', { count: pagePersonIds.length.toString() })}
                        >
                            {t('BULK_EXPORT_PAGE', { count: pagePersonIds.length.toString() })}
                        </Button>
                        <Button
                            size="compact-sm"
                            color="brand.7"
                            variant="default"
                            leftSection={<IconDownload style={{ width: rem(14), height: rem(14) }} />}
                            onClick={() => openExport('all')}
                            styles={{
                                root: {
                                    background: 'rgba(255, 255, 255, 0.56)',
                                    color: 'var(--mantine-color-brand-7)',
                                    border: '1px solid var(--mantine-color-orange-5)',
                                },
                                section: {
                                    color: 'var(--mantine-color-brand-7)',
                                },
                            }}
                            aria-label={t('BULK_EXPORT_ALL_FOUND_ARIA', { count: totalItems.toString() })}
                        >
                            {t('BULK_EXPORT_ALL_FOUND', { count: totalItems.toString() })}
                        </Button>
                        <Button
                            size="compact-sm"
                            variant="subtle"
                            color="dark.8"
                            leftSection={<IconX style={{ width: rem(14), height: rem(14) }} />}
                            onClick={onCancel}
                            styles={{
                                root: {
                                    background: 'transparent',
                                    color: 'var(--mantine-color-dark-8)',
                                    border: '1px solid transparent',
                                },
                                section: {
                                    color: 'var(--mantine-color-dark-8)',
                                },
                            }}
                            aria-label={t('BULK_CANCEL_SELECTION_MODE')}
                        >
                            {t('BULK_CANCEL')}
                        </Button>
                    </Group>
                </Group>
            </Box>

            {exportMode === 'selected' && (
                <CvExportOptionsModal
                    opened={exportModalOpen}
                    onClose={() => setExportModalOpen(false)}
                    title={t('BULK_MODAL_TITLE_SELECTED', { count: selectedCount.toString(), suffix: selectedCount !== 1 ? 's' : '' })}
                    count={selectedCount}
                    countMode="selected"
                    fileNamePrefix="CVs_Selected"
                    onExport={exportSelectedCvs}
                    onExported={() => {
                        onClearAll();
                        onCancel();
                    }}
                    review={{
                        getIds: () => Array.from(selectedIds),
                        onIdsChanged: syncReviewedIdsToSelection,
                    }}
                />
            )}

            {exportMode === 'page' && (
                <CvExportOptionsModal
                    opened={exportModalOpen}
                    onClose={() => setExportModalOpen(false)}
                    title={t('BULK_MODAL_TITLE_PAGE', { count: pagePersonIds.length.toString(), suffix: pagePersonIds.length !== 1 ? 's' : '' })}
                    count={pagePersonIds.length}
                    countMode="selected"
                    fileNamePrefix="CVs_Page"
                    onExport={exportPageCvs}
                    review={{
                        getIds: () => pagePersonIds,
                        onIdsChanged: syncReviewedIdsToSelection,
                    }}
                />
            )}

            {exportMode === 'all' && (
                <CvExportOptionsModal
                    opened={exportModalOpen}
                    onClose={() => setExportModalOpen(false)}
                    title={t('BULK_MODAL_TITLE_ALL', { count: totalItems.toString(), suffix: totalItems !== 1 ? 's' : '' })}
                    count={totalItems}
                    countMode="all-found"
                    fileNamePrefix="CVs_AllMatching"
                    onExport={exportAllCvs}
                    review={{
                        getIds: exportAllReviewIds,
                        onIdsChanged: syncReviewedIdsToSelection,
                    }}
                />
            )}
        </>
    );
};
